import * as THREE from 'three';

/**
 * Sky.ts — a gradient sky dome that FOLLOWS the player (§6.5).
 *
 * A sky dome fixed at the origin gets "driven out of" on a long course, leaving
 * a gap at the horizon. Instead this dome is recentred on the camera every frame
 * (and its gradient is computed relative to that centre), so you can never reach
 * its edge. It also ignores fog and renders behind everything.
 */
export class Sky {
  readonly mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;

  constructor(topColor: number, horizonColor: number, radius = 1000) {
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(topColor) },
        bottomColor: { value: new THREE.Color(horizonColor) },
        center: { value: new THREE.Vector3() },
        offset: { value: 0.08 },
        exponent: { value: 0.8 },
      },
      vertexShader: /* glsl */ `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform vec3 center;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          vec3 dir = normalize(vWorldPosition - center);
          float f = pow(clamp(dir.y + offset, 0.0, 1.0), exponent);
          gl_FragColor = vec4(mix(bottomColor, topColor, f), 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });

    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, 32, 16), this.material);
    this.mesh.frustumCulled = false; // never cull — it surrounds the camera
    this.mesh.renderOrder = -1; // draw before everything else
  }

  /** Recentre the dome on the camera each frame. */
  update(position: THREE.Vector3): void {
    this.mesh.position.copy(position);
    (this.material.uniforms.center.value as THREE.Vector3).copy(position);
  }

  /** Re-theme the gradient in place (used when switching cities). */
  setColors(topColor: number, horizonColor: number): void {
    (this.material.uniforms.topColor.value as THREE.Color).set(topColor);
    (this.material.uniforms.bottomColor.value as THREE.Color).set(horizonColor);
  }
}
